import { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Banknote, Receipt, TrendingUp, Package, Boxes, FileDown } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Common";

function todayIso(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [tab, setTab] = useState("sales");
  const [start, setStart] = useState(todayIso(-30));
  const [end, setEnd] = useState(todayIso(0));
  const [sales, setSales] = useState(null);
  const [kardex, setKardex] = useState(null);

  const fetchSales = useCallback(() => api.get(`/reports/sales?start=${start}&end=${end}`).then((r) => setSales(r.data)), [start, end]);
  const fetchKardex = useCallback(() => api.get("/reports/kardex-valuation").then((r) => setKardex(r.data)), []);

  useEffect(() => { if (tab === "sales") fetchSales(); }, [tab, fetchSales]);
  useEffect(() => { if (tab === "kardex") fetchKardex(); }, [tab, fetchKardex]);

  const maxDaily = useMemo(() => {
    if (!sales?.daily?.length) return 1;
    return Math.max(...sales.daily.map((d) => d.total), 1);
  }, [sales]);

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reportes"
        subtitle="Análisis de ventas e inventario valorizado."
        testid="reports-header"
      />

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-sales"
          onClick={() => setTab("sales")}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${tab === "sales" ? "bg-[#2A3F35] text-[#FAFAF8]" : "bg-[#F3F2ED] text-[#2A3F35]"}`}>
          Ventas
        </button>
        <button data-testid="tab-kardex"
          onClick={() => setTab("kardex")}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${tab === "kardex" ? "bg-[#2A3F35] text-[#FAFAF8]" : "bg-[#F3F2ED] text-[#2A3F35]"}`}>
          Kardex valorizado
        </button>
        <button data-testid="tab-ats"
          onClick={() => setTab("ats")}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${tab === "ats" ? "bg-[#2A3F35] text-[#FAFAF8]" : "bg-[#F3F2ED] text-[#2A3F35]"}`}>
          ATS (SRI)
        </button>
      </div>

      {tab === "ats" && <AtsTab />}

      {tab === "sales" && (
        <>
          <div className="card-soft p-5 mb-5 flex flex-wrap items-end gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5">Desde</p>
              <div className="relative">
                <Calendar className="w-4 h-4 text-[#8A9A92] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input data-testid="report-start" type="date" className="input-soft pl-9" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5">Hasta</p>
              <div className="relative">
                <Calendar className="w-4 h-4 text-[#8A9A92] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input data-testid="report-end" type="date" className="input-soft pl-9" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 ml-auto">
              {[
                ["Hoy", 0, 0],
                ["7d", -7, 0],
                ["30d", -30, 0],
                ["90d", -90, 0],
              ].map(([l, s, e]) => (
                <button key={l} className="btn-secondary text-sm" onClick={() => { setStart(todayIso(s)); setEnd(todayIso(e)); }}>{l}</button>
              ))}
            </div>
          </div>

          {sales && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <Kpi label="Ventas" value={`$ ${sales.summary.total_sales.toFixed(2)}`} icon={Banknote} color="#507A59" />
                <Kpi label="IVA generado" value={`$ ${sales.summary.total_iva.toFixed(2)}`} icon={TrendingUp} color="#2A3F35" />
                <Kpi label="Facturas" value={sales.summary.total_invoices} icon={Receipt} color="#608090" />
                <Kpi label="Ticket promedio" value={`$ ${sales.summary.average_ticket.toFixed(2)}`} icon={Banknote} color="#84978F" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <div className="card-soft p-6">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92] mb-4">Ventas por día</p>
                  {sales.daily.length === 0 ? (
                    <p className="text-sm text-[#5C6B64]">Sin ventas en el período.</p>
                  ) : (
                    <div className="space-y-2">
                      {sales.daily.map((d) => (
                        <div key={d.date} className="flex items-center gap-3 text-sm">
                          <span className="text-xs text-[#5C6B64] w-20 font-mono">{d.date.slice(5)}</span>
                          <div className="flex-1 h-7 bg-[#F3F2ED] rounded-md overflow-hidden">
                            <div className="h-full bg-[#84978F]" style={{ width: `${(d.total / maxDaily) * 100}%` }} />
                          </div>
                          <span className="w-20 text-right font-bold">$ {d.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="card-soft p-6">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92] mb-4">Top productos</p>
                  {sales.top_products.length === 0 ? (
                    <p className="text-sm text-[#5C6B64]">Sin datos.</p>
                  ) : (
                    <div className="space-y-3">
                      {sales.top_products.map((p) => (
                        <div key={p.product_id} className="flex items-center justify-between text-sm gap-3">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{p.name}</p>
                            <p className="text-xs text-[#8A9A92] font-mono">{p.sku} · {p.quantity.toFixed(2)} u.</p>
                          </div>
                          <span className="font-bold">$ {p.revenue.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card-soft overflow-x-auto" data-testid="sales-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                    <th className="px-5 py-3">Factura</th><th className="px-5 py-3">Cliente</th>
                    <th className="px-5 py-3">Fecha</th><th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3 text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {sales.invoices.map((i) => (
                      <tr key={i.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                        <td className="px-5 py-3 font-mono">{i.document_number}</td>
                        <td className="px-5 py-3">{i.customer_name}</td>
                        <td className="px-5 py-3 text-xs text-[#5C6B64]">{new Date(i.issue_date).toLocaleString()}</td>
                        <td className="px-5 py-3"><StatusChip status={i.status} /></td>
                        <td className="px-5 py-3 text-right font-bold">$ {Number(i.total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === "kardex" && kardex && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
            <Kpi label="Valor total inventario" value={`$ ${kardex.total_inventory_value.toFixed(2)}`} icon={Boxes} color="#2A3F35" />
            <Kpi label="Productos" value={kardex.products.length} icon={Package} color="#84978F" />
            <Kpi label="Bajo mínimo" value={kardex.products.filter((p) => p.below_min).length} icon={Package} color="#A44A4A" />
          </div>
          <div className="card-soft overflow-x-auto" data-testid="kardex-valuation-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">SKU</th><th className="px-5 py-3">Producto</th>
                <th className="px-5 py-3 text-right">Stock</th>
                <th className="px-5 py-3 text-right">Costo unit. prom.</th>
                <th className="px-5 py-3 text-right">Valor total</th>
              </tr></thead>
              <tbody>
                {kardex.products.map((p) => (
                  <tr key={p.product_id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                    <td className="px-5 py-3 font-mono">{p.sku}</td>
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-5 py-3 text-right">
                      {p.stock.toFixed(2)} {p.below_min && <span className="text-[#A44A4A] text-xs ml-1">bajo</span>}
                    </td>
                    <td className="px-5 py-3 text-right">$ {p.avg_cost.toFixed(4)}</td>
                    <td className="px-5 py-3 text-right font-bold">$ {p.total_value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between">
        <p className="text-xs uppercase tracking-[0.2em] text-[#8A9A92]">{label}</p>
        <div className="w-8 h-8 rounded-md grid place-items-center" style={{ background: `${color}22` }}>
          <Icon className="w-[16px] h-[16px]" strokeWidth={1.7} style={{ color }} />
        </div>
      </div>
      <p className="font-heading text-2xl font-black mt-3">{value}</p>
    </div>
  );
}

function AtsTab() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const url = (fmt) => `${API_BASE}/ats?year=${year}&month=${month}&format=${fmt}`;
  return (
    <div className="card-soft p-7">
      <h3 className="font-heading text-xl font-bold mb-2">Anexo Transaccional Simplificado</h3>
      <p className="text-sm text-[#5C6B64] mb-5">
        Genera el archivo mensual con todas las ventas autorizadas, retenciones emitidas y comprobantes anulados.
        El XML está listo para subir al portal DIMM/ATS del SRI.
      </p>
      <div className="flex flex-wrap gap-4 items-end mb-6">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Año</span>
          <input data-testid="ats-year" type="number" min="2020" max="2100" className="input-soft w-28" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Mes</span>
          <select data-testid="ats-month" className="input-soft w-40" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, "0")} — {new Date(2000, m - 1, 1).toLocaleString("es", { month: "long" })}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a data-testid="ats-download-xml" href={url("xml")} target="_blank" rel="noreferrer"
          className="card-soft p-5 hover:border-[#2A3F35] transition no-underline text-inherit">
          <FileDown className="w-7 h-7 text-[#2A3F35] mb-3" strokeWidth={1.7} />
          <p className="font-heading font-bold">XML ATS oficial</p>
          <p className="text-xs text-[#5C6B64] mt-1">Para subir al portal DIMM del SRI</p>
        </a>
        <a data-testid="ats-download-excel" href={url("excel")} target="_blank" rel="noreferrer"
          className="card-soft p-5 hover:border-[#2A3F35] transition no-underline text-inherit">
          <FileDown className="w-7 h-7 text-[#507A59]" strokeWidth={1.7} />
          <p className="font-heading font-bold">Excel (.xlsx)</p>
          <p className="text-xs text-[#5C6B64] mt-1">3 hojas: resumen, ventas, retenciones</p>
        </a>
        <a data-testid="ats-download-pdf" href={url("pdf")} target="_blank" rel="noreferrer"
          className="card-soft p-5 hover:border-[#2A3F35] transition no-underline text-inherit">
          <FileDown className="w-7 h-7 text-[#608090]" strokeWidth={1.7} />
          <p className="font-heading font-bold">PDF resumen</p>
          <p className="text-xs text-[#5C6B64] mt-1">Para revisión rápida con tu contadora</p>
        </a>
      </div>
    </div>
  );
}
